// Shared modal dialog for the HMI: the single source of truth for confirm and
// informational pop-ups (logout, queue removal, deletes, Insights dialogs).
// Form-style dialogs (Orders "+ New Order", Admin "Add User") deliberately do
// not use this component.

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  CircularProgress,
  Box,
} from '@mui/material';
import { tokens } from '../theme';

const ACCENT_KEYS = {
  // Direct accent names — pass the accent of the button that opened the popup.
  teal: 'tealAccent',
  red: 'redAccent',
  orange: 'orangeAccent',
  purple: 'purpleAccent',
  blue: 'blueAccent',
  green: 'greenAccent',
  grey: 'grey',
  // Legacy semantic names (old ConfirmDialog API).
  primary: 'tealAccent',
  success: 'tealAccent',
  warning: 'orangeAccent',
  error: 'redAccent',
  danger: 'redAccent',
};

/**
 * Resolves an accent name or hex string to base/hover/contrast colors.
 * @param {object} colors Theme token palette.
 * @param {string} accent Accent name from ACCENT_KEYS or a "#hex" string.
 * @returns {{base: string, hover: string, contrast: string}} Button colors,
 *   defaulting to teal for unknown values.
 */
function resolveAccentHex(colors, accent) {
  const tokenKey = ACCENT_KEYS[accent];
  if (tokenKey && colors[tokenKey]) {
    return {
      base: colors[tokenKey][500],
      hover: colors[tokenKey][600] || colors[tokenKey][500],
      contrast: '#fff',
    };
  }
  if (typeof accent === 'string' && accent.startsWith('#')) {
    return { base: accent, hover: accent, contrast: '#fff' };
  }
  return {
    base: colors.tealAccent[500],
    hover: colors.tealAccent[600] || colors.tealAccent[500],
    contrast: '#fff',
  };
}

// Same width/height for both buttons so they read as a balanced pair.
const ACTION_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 700,
  minWidth: 120,
  height: 38,
  borderRadius: '6px',
  boxShadow: 'none',
};

/**
 * App-standard pop-up. With `onConfirm` it renders Cancel + a filled confirm
 * button; without it, a single filled Close button (info mode).
 *
 * @param {boolean} open Whether the dialog is shown.
 * @param {Function} onClose Called on close/cancel.
 * @param {Function} [onConfirm] Confirm handler; presence switches to confirm mode.
 * @param {string} [title] Dialog title.
 * @param {string|React.ReactNode} [message] Body text or node; `children` also render.
 * @param {string} [confirmLabel] Confirm button label.
 * @param {string} [cancelLabel] Cancel button label.
 * @param {string} [closeLabel] Close button label (info mode).
 * @param {string} [accent] Accent name or "#hex" — should match the color of
 *   the button that opened the pop-up. Defaults to teal.
 * @param {string} [confirmColor] Legacy alias for `accent`.
 * @param {number} [maxWidth] Dialog width in px; pass larger for wide tables.
 * @param {boolean} [busy] Disables buttons and shows a spinner during requests.
 * @returns {JSX.Element} The dialog.
 */
export default function Popup({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  closeLabel = 'Close',
  accent,
  confirmColor, // legacy alias for accent
  maxWidth = 520,
  busy = false,
}) {
  const theme = useTheme();
  const colors = useMemo(() => tokens(theme.palette.mode), [theme.palette.mode]);
  const isDark = theme.palette.mode === 'dark';

  const accentName = accent || confirmColor || 'teal';
  const accentHex = useMemo(
    () => resolveAccentHex(colors, accentName),
    [colors, accentName],
  );

  const isConfirm = typeof onConfirm === 'function';

  // Dark mode uses primary[300], the same card grey as the tables.
  const dialogBg = isDark ? colors.primary[300] : '#fff';
  const bodyTextColor = isDark ? colors.grey[900] : colors.grey[900];

  const cancelBorder = isDark ? colors.grey[400] : colors.grey[600];
  const cancelText = isDark ? colors.grey[900] : colors.grey[800];
  const cancelHoverBg = isDark ? colors.grey[500] : colors.grey[200];

  const handleConfirm = () => {
    if (busy) return;
    if (typeof onConfirm === 'function') onConfirm();
  };

  const handleClose = (_event, reason) => {
    if (busy && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    if (typeof onClose === 'function') onClose();
  };

  const filledButtonSx = {
    ...ACTION_BUTTON_SX,
    backgroundColor: accentHex.base,
    color: accentHex.contrast,
    '&:hover': {
      backgroundColor: accentHex.hover,
      boxShadow: 'none',
      filter: 'brightness(0.95)',
    },
    '&:active': {
      filter: 'brightness(0.9)',
    },
    '&.Mui-disabled': {
      backgroundColor: accentHex.base,
      color: accentHex.contrast,
      opacity: 0.6,
    },
  };

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      // Standard app-wide width; text wraps inside rather than stretching the dialog.
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: `min(${maxWidth}px, 92vw)`,
          borderRadius: '12px',
          backgroundColor: dialogBg,
          backgroundImage: 'none',
        },
      }}
      aria-labelledby="popup-title"
      aria-describedby="popup-description"
    >
      <DialogTitle
        id="popup-title"
        sx={{
          fontWeight: 700,
          color: accentHex.base,
          pb: 0.5,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pb: 1, overflowY: 'auto' }}>
        {typeof message === 'string' ? (
          <DialogContentText
            id="popup-description"
            sx={{ color: bodyTextColor }}
          >
            {message}
          </DialogContentText>
        ) : message ? (
          <Box id="popup-description" sx={{ color: bodyTextColor }}>
            {message}
          </Box>
        ) : null}
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.25, pt: 1, gap: 1.25 }}>
        {isConfirm ? (
          <>
            <Button
              onClick={handleClose}
              disabled={busy}
              variant="outlined"
              sx={{
                ...ACTION_BUTTON_SX,
                color: cancelText,
                borderColor: cancelBorder,
                backgroundColor: 'transparent',
                '&:hover': {
                  borderColor: cancelText,
                  backgroundColor: cancelHoverBg,
                },
                '&.Mui-disabled': {
                  borderColor: cancelBorder,
                  color: cancelText,
                  opacity: 0.6,
                },
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={busy}
              variant="contained"
              disableElevation
              autoFocus
              startIcon={busy
                ? <CircularProgress size={14} sx={{ color: accentHex.contrast }} />
                : null}
              sx={filledButtonSx}
            >
              {confirmLabel}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="contained"
            disableElevation
            autoFocus
            sx={filledButtonSx}
          >
            {closeLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
